/* Product gallery zoom (PhotoSwipe).
   Rewritten 2026-10-08 for INP (Round 1, T1+T2):
   - One delegated click/keyup listener on the <zoom-images> root instead of
     two listeners per gallery image (31 figures -> 2 listeners).
   - PhotoSwipe is downloaded/parsed during idle after load instead of on the
     first image tap (warm), and the item list is built once.
   - The lightbox placeholder (msrc) now points at the already-cached gallery
     image instead of the full-size file, so the zoom opens instantly while
     the large image streams in.
   Public contract unchanged: element <zoom-images>, children [data-zoom-image]
   with data-image-src / data-image-width / data-image-height. */
!(function () {
  'use strict';

  const ZOOM_IMAGE = '[data-zoom-image]';

  class ZoomImages extends HTMLElement {
    constructor() {
      super();
      this.items = null;
      this.indexMap = null;
      this.handleClick = (event) => this.onClick(event);
      this.handleKeyup = (event) => this.onKeyup(event);
    }

    connectedCallback() {
      // Query here, not in the constructor: children AND ancestors are only
      // guaranteed present at connect time — a programmatically created
      // element runs its constructor before the gallery children exist and
      // before it has the [data-section-id] ancestor. Drop any cached item
      // list from a previous connection so a re-rendered gallery rebuilds
      // from the current images (buildItems is lazy and guards on this.items).
      this.images = this.querySelectorAll(ZOOM_IMAGE);
      this.container = this.closest('[data-section-id]');
      this.zoomCaptions = this.container ? this.container.querySelector('[data-zoom-caption]') : null;
      this.thumbsContainer = document.querySelector('.pswp__thumbs');
      this.items = null;
      this.indexMap = null;
      if (this.images.length === 0) return;
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keyup', this.handleKeyup);
      this.scheduleWarm();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keyup', this.handleKeyup);
    }

    onClick(event) {
      const target = event.target.closest(ZOOM_IMAGE);
      if (!target) return;
      event.preventDefault();
      if (target.closest('product-images') && target.closest('product-images').classList.contains('is-dragging')) return;
      const index = this.indexOfImage(target);
      if (index === -1) return;
      window.a11y.lastElement = target;
      // Yield before building/opening the lightbox: the click interaction's
      // duration ends at the next paint, and PhotoSwipe init + first-image
      // render otherwise run inside that window (measured +16-80ms at 4x CPU
      // throttle). The timeout fires right after the frame renders, so the
      // lightbox still opens essentially instantly.
      setTimeout(() => this.createZoom(index), 0);
    }

    onKeyup(event) {
      if (event.code !== 'Enter') return;
      const target = event.target.closest(ZOOM_IMAGE);
      if (!target) return;
      event.preventDefault();
      const index = this.indexOfImage(target);
      if (index === -1) return;
      window.a11y.lastElement = target;
      setTimeout(() => this.createZoom(index), 0);
    }

    indexOfImage(target) {
      if (!this.indexMap) this.buildItems();
      return this.indexMap.has(target) ? this.indexMap.get(target) : Array.prototype.indexOf.call(this.images, target);
    }

    /* Download + parse photoswipe.js during idle after load, and cache the
       slide list, so the first tap only pays the lightbox render. */
    scheduleWarm() {
      // Idempotent across re-connections: connectedCallback runs again on
      // section re-render, and an unguarded re-arm would stack load
      // listeners (review_r2 finding).
      if (this.warmScheduled) return;
      this.warmScheduled = true;
      const start = () => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => this.warm(), { timeout: 3500 });
        } else {
          setTimeout(() => this.warm(), 2500);
        }
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    warm() {
      // A warm scheduled before disconnection may fire on a detached
      // element — skip it. REVIEWER NOTE (deliberate, re-flagged twice —
      // the previous note's "a reconnect re-schedules if needed" was
      // WRONG, which is why reviewers kept flagging it): warmScheduled is
      // NOT cleared here or on disconnect, so after a detach-then-reconnect
      // warm() genuinely never re-runs. That is accepted on purpose:
      // connectedCallback nulls this.items (they rebuild lazily on first
      // open) and warmPhotoswipe is only a preload nicety for the
      // module-cached PhotoSwipe — worst case is a cold first open after
      // that editor-shaped edge, never a broken one. Clearing the flag
      // would re-run warm post-reconnect only to redo cached work.
      if (!this.isConnected) return;
      this.buildItems();
      if (window.theme && typeof window.theme.warmPhotoswipe === 'function') {
        window.theme.warmPhotoswipe();
      }
    }

    buildItems() {
      if (this.items) return this.items;
      this.items = [];
      this.indexMap = new Map();
      this.images.forEach((el, index) => {
        this.indexMap.set(el, index);
        this.items.push({
          src: el.getAttribute('data-image-src'),
          w: parseInt(el.getAttribute('data-image-width'), 10),
          h: parseInt(el.getAttribute('data-image-height'), 10),
          el: el,
        });
      });
      return this.items;
    }

    createZoom(index) {
      const items = this.buildItems().map((item) => {
        const galleryImg = item.el ? item.el.querySelector('img') : null;
        const candidate = (galleryImg && (galleryImg.currentSrc || galleryImg.getAttribute('src'))) || '';
        // a not-yet-loaded lazy image can report the inline SVG placeholder
        // as src — only use it when it is a real URL
        const msrc = /^https?:/.test(candidate) ? candidate : item.src;
        return { src: item.src, w: item.w, h: item.h, msrc: msrc };
      });

      const thumbsHTML = this.container && this.container.querySelector('[data-pswp-thumbs-template]')
        ? this.container.querySelector('[data-pswp-thumbs-template]').innerHTML
        : '';
      const scheme = this.getColorScheme();
      const options = {
        history: false,
        focus: false,
        index: index,
        mainClass:
          'pswp-zoom-gallery' +
          (items.length === 1 ? ' pswp-zoom-gallery--single' : '') +
          (scheme ? ' ' + scheme : ''),
        showHideOpacity: true,
        showAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ['caption-close', 'title'],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: true,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: false,
        addCaptionHTMLFn: (item, el, isFake) => this.zoomCaption(item, el, isFake),
        getThumbBoundsFn: (pswpIndex) => {
          // PhotoSwipe passes the CURRENT slide index (thumbnail clicks,
          // loop navigation) — with loop:true, closing on any image other
          // than the opening one must zoom toward THAT image's rect, not
          // the opening index we captured at open time.
          const image = this.images[typeof pswpIndex === 'number' ? pswpIndex : index];
          // Detached/missing image (gallery re-rendered between open and
          // callback): return undefined so PhotoSwipe uses its fallback
          // transition instead of animating from a zero rect.
          if (!image || !image.isConnected) return;
          const pageYScroll = window.scrollY || document.documentElement.scrollTop;
          const rect = image.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        },
      };

      new window.theme.LoadPhotoswipe(items, options);

      if (this.thumbsContainer && '' !== thumbsHTML) {
        this.thumbsContainer.innerHTML = thumbsHTML;
      }
    }

    zoomCaption(item, el) {
      let captionMarkup = '';
      const captionEl = el.children[0];

      if (this.zoomCaptions) {
        captionMarkup = this.zoomCaptions.innerHTML;
        if (this.zoomCaptions.closest('.variant--soldout')) captionEl.classList.add('variant--soldout');
        else captionEl.classList.remove('variant--soldout');
        if (this.zoomCaptions.closest('.variant--unavailable')) captionEl.classList.add('variant--unavailable');
        else captionEl.classList.remove('variant--unavailable');
      }

      captionEl.innerHTML = captionMarkup;
      return false;
    }

    getColorScheme() {
      const info = this.closest('product-info');
      return info ? Array.from(info.classList).find((cls) => cls.startsWith('color-scheme')) : null;
    }
  }

  if (!customElements.get('zoom-images')) {
    customElements.define('zoom-images', ZoomImages);
  }
})();
